import {
  Database,
  LayoutDashboard,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import type { OnboardingStepId } from "@/contexts/OnboardingContext";
import { ONBOARDING_STEPS, useOnboarding } from "@/contexts/OnboardingContext";

// ── Step card icon map ────────────────────────────────────────────────────────

const STEP_ICONS: Record<OnboardingStepId, React.ReactNode> = {
  "general-settings": <Settings className="h-5 w-5 text-primary" />,
  "data-sources": <Database className="h-5 w-5 text-primary" />,
  "roles-permissions": <Shield className="h-5 w-5 text-primary" />,
  "user-management": <Users className="h-5 w-5 text-primary" />,
  "dashboard-layout": <LayoutDashboard className="h-5 w-5 text-primary" />,
};

// ── Welcome screen ────────────────────────────────────────────────────────────

interface WelcomeScreenProps {
  firstName: string;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ firstName }) => {
  const { nextStep, dismissOnboarding } = useOnboarding();

  return (
    <div className="flex items-center justify-center min-h-full p-8">
      <div className="w-full max-w-2xl">
        {/* App logo + name */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-sm">
            <span className="text-primary-foreground text-2xl font-bold">
              CF
            </span>
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            Access Cash Forecaster
          </h1>
        </div>

        {/* Greeting */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Welcome, {firstName}! 👋
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-lg mx-auto">
            Let's get your workspace set up. This assistant will guide you
            through the key configuration steps. It only takes a few minutes and
            you can always come back to it later.
          </p>
        </div>

        {/* Step preview cards — 2-column grid */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {ONBOARDING_STEPS.map((step) => (
            <div
              key={step.id}
              className="flex items-start gap-3 p-4 rounded-lg border border-border bg-white hover:bg-muted/30 transition-colors cursor-default"
            >
              {/* Icon in rounded square */}
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                {STEP_ICONS[step.id]}
              </div>
              {/* Name + description */}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {step.name}
                </p>
                <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                  {step.cardDescription}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3">
          <Button size="lg" onClick={nextStep} className="px-8">
            Let's Start →
          </Button>
          <button
            type="button"
            onClick={dismissOnboarding}
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
          >
            Skip onboarding
          </button>
        </div>
      </div>
    </div>
  );
};
